import icn_line_chart from "assets/images/icons/icn_line_chart.png";

import React, { useMemo, useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import { v4 as uuidv4 } from "uuid";

import useTableInstance from "hooks/useTableInstance";
import useDataSourceManager from "hooks/useDataSourceManager";

import CollapseCard from "components/Collapse";
import TableField from "components/Table";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";

import WirelessCongestionLevelSliderChart from "pages/AInsight/LinkIntegrity/charts/WirelessCongestionLevelSliderChart";

const BottomActionBar = ({
  parentId,
  show,
  onCheckFormValid,
  onCancel,
  onApply,
}) => {
  useEffect(() => {
    const parentElement = document.getElementById(parentId);
    const bottomActionBarElement = document.getElementById("bottomActionBar");

    if (!parentElement || !bottomActionBarElement) return;

    const updateWidth = () => {
      bottomActionBarElement.style.width = `${parentElement.clientWidth - 32}px`;
    };

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(parentElement);
    updateWidth();

    return () => {
      resizeObserver.disconnect();
    };
  }, [parentId]);

  return (
    <Box
      id="bottomActionBar"
      sx={{
        position: "fixed",
        bottom: "20px",
        width: 0,
        display: show ? "flex" : "none",
        justifyContent: "center",
        gap: 2,
        margin: "16px 0",
        padding: "16px",
        backgroundColor: "primary.lightBlue",
        transform: show ? "translateY(0)" : "translateY(80px)",
        transition: "transform 0.3s ease-in-out",
        zIndex: 1300,
      }}
    >
      <OutlineButton
        onClick={onCancel}
        sx={{ marginRight: "20px" }}
        width="100px"
        height="32px"
      >
        Cancel
      </OutlineButton>

      <SolidButton
        onClick={onApply}
        width="100px"
        height="32px"
        disabled={!onCheckFormValid()}
      >
        Apply
      </SolidButton>
    </Box>
  );
};

const WirelessCongestionLevelDrawer = ({
  wirelessCongestionChartSeriesData,
  wirelessCongestionDeviceApiChartData,
  analysisData,
  auth,
  onRefreshWirelessCongestion,
}) => {
  const theme = useTheme();
  const { sendRequest } = useDataSourceManager();

  const [apData, setApData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [threshold, setThreshold] = useState(analysisData?.threshold);
  const [originalThreshold, setOriginalThreshold] = useState(
    analysisData?.threshold,
  );
  const [showBottomBar, setShowBottomBar] = useState(false);

  const columnHelper = useMemo(() => createColumnHelper(), []);

  const apColumns = useMemo(
    () => [
      columnHelper.accessor("deviceName", {
        header: "AP Name",
        cell: (info) => info.getValue() ?? "-",
      }),
      columnHelper.accessor("timeCongestedPercent", {
        header: "％ Time Congested",
        cell: (info) => `${info.getValue() ?? 0}%`,
      }),
    ],
    [columnHelper],
  );

  useEffect(() => {
    const defaultThreshold = analysisData?.threshold ?? 30;

    setThreshold(defaultThreshold);
    setOriginalThreshold(defaultThreshold);
    setShowBottomBar(false);
  }, [analysisData?.threshold]);

  useEffect(() => {
    setLoading(true);

    const data = Array.isArray(wirelessCongestionDeviceApiChartData)
      ? wirelessCongestionDeviceApiChartData
      : wirelessCongestionDeviceApiChartData
        ? [wirelessCongestionDeviceApiChartData]
        : [];

    const devices = data.flatMap((item) =>
      Array.isArray(item.devices) ? item.devices : [],
    );

    setApData(devices);
    setLoading(false);
  }, [wirelessCongestionDeviceApiChartData]);


  const apTable = useTableInstance({
    tableName: "wirelessCongestionDrawerTable",
    columns: apColumns,
    data: apData,
    enableSorting: false,
    enablePagination: false,
  });

  const handleThresholdChange = (value) => {
    setThreshold(value);
    setShowBottomBar(value !== originalThreshold);
  };

  const handleCancel = () => {
    setThreshold(originalThreshold);
    setShowBottomBar(false);
  };

  const handleApply = async () => {
    try {
      const apiPath = auth?.orgs?.id
        ? "/ents/:entId/orgs/:orgId/sites/:siteId/linkIntegrity/wirelessCongestion/threshold.update"
        : "/orgs/:orgId/sites/:siteId/linkIntegrity/wirelessCongestion/threshold.update";

      const restfulParams = auth?.orgs?.id
        ? {
            entId: auth?.realm?.id,
            orgId: auth?.orgs?.id,
            siteId: auth?.sites?.id,
          }
        : {
            orgId: auth?.realm?.id,
            siteId: auth?.sites?.id,
          };

      const requestParams = {
        data: {
          threshold,
        },
      };

      await sendRequest(apiPath, uuidv4(), restfulParams, requestParams);

      onRefreshWirelessCongestion?.(threshold);

      setOriginalThreshold(threshold);
      setShowBottomBar(false);
    } catch (error) {
      console.error("Failed to update wireless congestion threshold:", error);
    }
  };

  return (
    <Box
      id="wirelessCongestionLevelDrawer"
      sx={{
        p: 3,
        display: "flex",
        flexDirection: "column",
        gap: 3,
        pb: showBottomBar ? 12 : 3,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box
          component="img"
          src={icn_line_chart}
          sx={{ width: 20, height: 20, mr: 1.5 }}
        />

        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontSize: theme.typography.customFontSize[3],
          }}
        >
          Wireless Congestion Level
        </Typography>
      </Box>

      <CollapseCard title="Wireless Congestion">
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <WirelessCongestionLevelSliderChart
            data={wirelessCongestionChartSeriesData}
            defaultThreshold={analysisData?.threshold ?? 30}
            externalThreshold={threshold}
            onThresholdChange={handleThresholdChange}
          />
        </Box>
      </CollapseCard>

      <CollapseCard title="Congested APs (Last 24h)">
        <Box sx={{ width: "100%" }}>
          <TableField {...apTable} loading={loading} />
        </Box>
      </CollapseCard>

      <BottomActionBar
        parentId="wirelessCongestionLevelDrawer"
        show={showBottomBar}
        onCheckFormValid={() => threshold !== originalThreshold}
        onCancel={handleCancel}
        onApply={handleApply}
      />
    </Box>
  );
};

export default WirelessCongestionLevelDrawer;
